/**
 * Owns the lifetime of the authenticated session.
 *
 * Design rules:
 *  1. At most one refresh in flight per tab (single-flight promise) and at most
 *     one per origin (cross-tab lock). There is no "forced" path that skips it:
 *     concurrent refreshes spend the same refresh token twice and make the auth
 *     server invalidate the whole token family.
 *  2. The schedule is derived from the token's own `exp`, bounded by the
 *     configured interval, so a shorter backend TTL cannot open a dead window.
 *  3. A refresh is only allowed to end the session once the access token it was
 *     meant to replace is itself dead. A grant the server refuses costs the
 *     ability to renew, not the session: the current token is still accepted for
 *     the rest of its life, and the API's own 401 handling ends the session if
 *     it turns out not to be. A refresh lost to the network is retried with
 *     backoff while the access token still lives.
 *  4. Ending the session is announced to every tab, so no tab is left holding a
 *     screen it can no longer talk to the API from.
 *  5. Every attempt reports *why* it produced no token. "Could not refresh right
 *     now" and "the server refused the grant" look identical to a caller that
 *     only sees a missing token, and the first one must never end the session.
 *  6. A valid access token is usable on its own; the refresh token only decides
 *     whether the session can be renewed later. Some roles' login grant carries
 *     no refresh token at all, and requiring one before handing out the access
 *     token meant every request from those roles went out with no Authorization
 *     header — a 401 on the very first call, not on the first refresh.
 *  7. A session that can still be recovered is never ended with a redirect while
 *     a reauthentication screen is mounted. The tab keeps its storage and every
 *     unsaved change on screen, the user logs in again on top of it, and the
 *     requests that were refused are replayed with the new token.
 */
import Axios from 'axios';
import { REACT_APP_ROUTE_HOME, REACT_APP_URL_V2_OAUTH_REFRESH_TOKEN } from '../../components/common/Routes/URLs';
import { BACKEND_IDS, getBackendBaseUrl, getBackendCredentials } from '../../services/ApiConfig';
import { withCrossTabLock } from './crossTabLock';
import { getExpiryMs, getIssuedAtMs, getMsUntilExpiry, hasLoginScope, isExpired } from './jwt';
import { buildOAuthFormHeaders } from './oauthHeaders';
import { getOAuthRefreshHttpTimeoutMs, getRefreshTokenIntervalSeconds } from './refreshIntervalConfig';
import { onSessionEvent, publishSessionEvent, SESSION_EVENTS } from './sessionChannel';
import { isRefreshTokenInvalidResponse, isSessionInactiveResponse, isSessionReplacedResponse } from './sessionErrors';
import { clearSession, commitTokens, getAccessToken, getRefreshToken, getTokenIssuedAt, getTokenVersion, hasTokens } from './sessionStore';

export { isSessionReplacedResponse, SESSION_REPLACED_KEY } from './sessionErrors';

const REFRESH_LOCK = 'tempedge-token-refresh';
const CHECK_INTERVAL_MS = 30000;
const CATCH_UP_DELAY_MS = 500;
const EXPIRY_SKEW_MS = 30000;
const MAX_NETWORK_RETRIES = 5;
const BASE_BACKOFF_MS = 2000;
const MAX_BACKOFF_MS = 60000;
const LOCK_TIMEOUT_MARGIN_MS = 5000;

/**
 * Why an attempt produced no new token. Only ENDED means the session is gone,
 * and by the time it is reported the teardown has already run. DEFERRED is a
 * "not now": the tokens in storage are still the good ones and the schedule
 * will try again.
 */
export const REFRESH_OUTCOMES = {
  REFRESHED: 'refreshed',
  UP_TO_DATE: 'up-to-date',
  DEFERRED: 'deferred',
  ENDED: 'ended'
};

let inflight = null;
let networkFailures = 0;
let blockedUntil = 0;
let checkTimer = null;
let catchUpTimer = null;
let started = false;
let ending = false;

export const REAUTH_REASON = {
  unauthorized: 'unauthorized',
  sessionReplaced: 'session_replaced',
  sessionNotValid: 'session_not_valid',
  idleLock: 'idle_lock',
  connectionLost: 'connection_lost'
};

// Reasons where the user can log in again from the same screen instead of being sent to the login page.
const REAUTH_REASONS = [...Object.values(REAUTH_REASON), 'refresh_rejected', 'refresh_unreachable'];

let reauthHosts = 0;
let reauthRequest = null;
let settleReauth = null;
let pendingReauthReason = null;
const reauthListeners = new Set();

let lastRefreshFailureReason = null;

/**
 * How long a token may be used before it is replaced: the configured interval,
 * capped at half its real lifetime. The cap is what makes a shorter backend TTL
 * safe — a fixed interval longer than the token's life leaves a dead window in
 * which every request comes back 401.
 */
const getRefreshAfterMs = (token) => {
  const configuredMs = getRefreshTokenIntervalSeconds() * 1000;
  const issuedAt = getIssuedAtMs(token);
  const expiry = getExpiryMs(token);

  if (issuedAt === null || expiry === null) return configuredMs;
  return Math.min(configuredMs, Math.max((expiry - issuedAt) / 2, EXPIRY_SKEW_MS));
};

/** Epoch ms the current token was issued, from its own claim or from storage. */
const getTokenAgeStart = (token) => getIssuedAtMs(token) ?? getTokenIssuedAt();

/**
 * True when the access token has been in use long enough to be worth replacing.
 * Reported to the outside through `getSessionInfo().refreshDue`.
 */
const isRefreshDue = () => {
  if (!hasTokens()) return false;

  const token = getAccessToken();
  const ageStart = getTokenAgeStart(token);
  if (ageStart === null) return true;

  return Date.now() - ageStart >= getRefreshAfterMs(token);
};

/**
 * True while the access token would still be accepted by the server. It is what
 * decides whether a failed refresh has actually cost the user their session:
 * losing the ability to renew is not the same as losing the session, and only
 * the second one is worth telling the user about.
 */
export const isSessionUsable = () => {
  const token = getAccessToken();
  return !!token && !isExpired(token, 0);
};

const redirectToLogin = () => {
  const lang = window.location.pathname.split('/')[2] || process.env.REACT_APP_DEFAULT_LANGUAGE || 'en';
  window.location.replace(REACT_APP_ROUTE_HOME(lang));
};

/** A new token from any tab makes this tab's backoff stale: the origin answers again. */
const clearBackoff = () => {
  networkFailures = 0;
  blockedUntil = 0;
};

/** Tears the session down locally without touching storage owned by other tabs. */
const teardown = () => {
  stopScheduler();
  inflight = null;
  clearBackoff();
};

/**
 * Ends the session everywhere: clears storage, tells the other tabs and returns
 * this one to the login screen. Safe to call more than once.
 *
 * `broadcast: false` is for the tab that is reacting to another tab's
 * announcement, so the same closing sequence is not echoed back around.
 */
export const endSession = (reason = 'unknown', { broadcast = true } = {}) => {
  if (ending) return;

  if (broadcast && canReauthFor(reason)) {
    requestReauth(reason);
    return;
  }

  ending = true;
  finishReauth(false);
  if (broadcast) publishSessionEvent(SESSION_EVENTS.SESSION_ENDED, { reason });
  teardown();
  clearSession();
  redirectToLogin();
};

const notifyReauth = (reason) => reauthListeners.forEach((listener) => listener(reason));

// A full session always ends up mounting the reauthentication screen; a request refused while the app is still
// booting (before the screen mounts) leaves the reauthentication pending for it instead of logging the user out.
export const canRequestReauth = () => reauthHosts > 0 || hasLoginScope(getAccessToken());

const canReauthFor = (reason) => REAUTH_REASONS.includes(reason) && canRequestReauth();

export const getReauthReason = () => pendingReauthReason;

/** Why the last refresh was refused, for the caller that has to tell the user. Cleared once read. */
export const takeRefreshFailureReason = () => {
  const reason = lastRefreshFailureReason;
  lastRefreshFailureReason = null;
  return reason;
};

export const isReauthPending = () => reauthRequest !== null;

// Keeps the tab, its storage and everything on screen while the user logs in again. Every caller shares
// one promise: true once new tokens are committed, false when the user gives up and the session ends.
export const requestReauth = (reason = 'unauthorized') => {
  if (reauthRequest) return reauthRequest;

  teardown();
  reauthRequest = new Promise((resolve) => {
    settleReauth = resolve;
  });
  pendingReauthReason = reason;
  notifyReauth(reason);
  return reauthRequest;
};

const finishReauth = (reauthenticated) => {
  if (!reauthRequest) return;

  const settle = settleReauth;
  reauthRequest = null;
  settleReauth = null;
  pendingReauthReason = null;
  notifyReauth(null);
  settle(reauthenticated);
};

export const completeReauth = (tokens) => {
  commitTokens(tokens);
  finishReauth(true);
  startSession();
};

export const cancelReauth = () => {
  finishReauth(false);
  endSession('reauth_cancelled');
};

export const registerReauthHost = () => {
  reauthHosts += 1;
  return () => {
    reauthHosts -= 1;
  };
};

export const onReauthChange = (listener) => {
  reauthListeners.add(listener);
  return () => reauthListeners.delete(listener);
};

/**
 * Sends the grant under both parameter names: this backend reads the camelCase
 * `refreshToken` and answers 401 when only the spec's `refresh_token` is present.
 */
const requestNewTokens = async (refreshToken) => {
  const credentials = getBackendCredentials(BACKEND_IDS.BACKEND_2);
  const baseUrl = getBackendBaseUrl(BACKEND_IDS.BACKEND_2);

  const body = new URLSearchParams({
    grant_type: 'refresh_token',
    refreshToken,
    refresh_token: refreshToken
  }).toString();

  const response = await Axios.post(`${baseUrl}${REACT_APP_URL_V2_OAUTH_REFRESH_TOKEN}`, body, {
    headers: buildOAuthFormHeaders(credentials),
    timeout: getOAuthRefreshHttpTimeoutMs()
  });

  return {
    accessToken: response.data?.accessToken || response.data?.access_token,
    refreshToken: response.data?.refreshToken || response.data?.refresh_token
  };
};

/**
 * A 400/401/403 without a session code means the server refused the grant.
 * Retrying only burns tokens. Transport failures are retried while the access
 * token is still alive. TE-E07 and TE-E02 are decided before this: those kill
 * the session even when the JWT has not expired yet.
 */
const isUnrecoverable = (error) => [400, 401, 403].includes(error?.response?.status);

/** Reason noted when a refresh failed because the server could not be reached, not because it refused. */
export const REFRESH_FAILURE_NETWORK = 'network';

/**
 * True when the last refresh failed on the network: the session is not lost, the connection is.
 * @author armandoricosandoval -ai-
 */
export const isRefreshNetworkFailure = () => lastRefreshFailureReason === REFRESH_FAILURE_NETWORK;

const noteRefreshFailure = (reason) => {
  lastRefreshFailureReason = reason;
};

/**
 * The tab that waits must outlast the tab that works. A lock timeout shorter
 * than the refresh HTTP timeout makes every other tab give up while the holder
 * is still legitimately in flight, and giving up is reported as DEFERRED.
 */
const getLockTimeoutMs = () => getOAuthRefreshHttpTimeoutMs() + LOCK_TIMEOUT_MARGIN_MS;

/**
 * Performs the one refresh allowed per origin.
 *
 * The `ending` check after the request is what stops a reply that arrives late
 * from writing tokens back into storage the logout has already cleared: the
 * promise cannot be cancelled, so the result is discarded instead.
 */
const runRefresh = async () => {
  const versionBefore = getTokenVersion();

  const { acquired, value } = await withCrossTabLock(
    REFRESH_LOCK,
    async () => {
      if (getTokenVersion() > versionBefore) return REFRESH_OUTCOMES.UP_TO_DATE;

      const refreshToken = getRefreshToken();
      if (!refreshToken) return REFRESH_OUTCOMES.ENDED;

      const tokens = await requestNewTokens(refreshToken);
      if (ending) return REFRESH_OUTCOMES.ENDED;
      if (!tokens.accessToken) return REFRESH_OUTCOMES.DEFERRED;

      commitTokens(tokens);
      return REFRESH_OUTCOMES.REFRESHED;
    },
    { timeoutMs: getLockTimeoutMs() }
  );

  return acquired ? value : REFRESH_OUTCOMES.DEFERRED;
};

/** Refresh answers that mean the server closed the session, with the reason the reauthentication screen shows. */
const REFRESH_CLOSING_REASONS = [
  [isSessionReplacedResponse, REAUTH_REASON.sessionReplaced],
  [isSessionInactiveResponse, REAUTH_REASON.sessionNotValid]
];

/** Records why the refresh failed and ends the session for that reason; always resolves to ENDED. */
const endSessionFor = (reason) => {
  noteRefreshFailure(reason);
  endSession(reason);
  return REFRESH_OUTCOMES.ENDED;
};

/**
 * A refused grant costs the ability to renew, not the session: while the access token still lives the
 * refresh is only deferred, and the session ends once that token is dead too.
 */
const deferWhileUsable = () => {
  if (isSessionUsable()) {
    blockedUntil = Date.now() + MAX_BACKOFF_MS;
    return REFRESH_OUTCOMES.DEFERRED;
  }

  endSession('refresh_rejected');
  return REFRESH_OUTCOMES.ENDED;
};

/**
 * Refreshes the token at most once per tab and once per origin. Concurrent
 * callers await the same promise instead of being dropped. Resolves to one of
 * `REFRESH_OUTCOMES`; callers must only treat ENDED as the end of the session.
 */
export const refreshOnce = ({ ignoreBackoff = false } = {}) => {
  if (inflight) return inflight;
  if (ending) return Promise.resolve(REFRESH_OUTCOMES.ENDED);
  if (!hasTokens()) return Promise.resolve(REFRESH_OUTCOMES.ENDED);
  if (!ignoreBackoff && Date.now() < blockedUntil) return Promise.resolve(REFRESH_OUTCOMES.DEFERRED);

  lastRefreshFailureReason = null;
  inflight = runRefresh()
    .then((outcome) => {
      if (outcome === REFRESH_OUTCOMES.REFRESHED || outcome === REFRESH_OUTCOMES.UP_TO_DATE) clearBackoff();

      return outcome;
    })
    .catch((error) => {
      const response = error.response;
      const closing = REFRESH_CLOSING_REASONS.find(([matches]) => matches(response));

      if (closing) return endSessionFor(closing[1]);

      if (isRefreshTokenInvalidResponse(response)) {
        noteRefreshFailure('refresh_rejected');
        return deferWhileUsable();
      }

      if (isUnrecoverable(error)) return deferWhileUsable();

      noteRefreshFailure(REFRESH_FAILURE_NETWORK);
      networkFailures += 1;
      blockedUntil = Date.now() + Math.min(BASE_BACKOFF_MS * 2 ** (networkFailures - 1), MAX_BACKOFF_MS);

      if (networkFailures >= MAX_NETWORK_RETRIES && !isSessionUsable()) {
        endSession('refresh_unreachable');
        return REFRESH_OUTCOMES.ENDED;
      }

      return REFRESH_OUTCOMES.DEFERRED;
    })
    .finally(() => {
      inflight = null;
    });

  return inflight;
};

/**
 * Guarantees the caller gets a usable access token.
 *
 * Only a token that is about to expire is worth waiting for: that request would
 * leave doomed, so the caller queues behind the refresh. A token that is merely
 * due for replacement is still accepted by the server, so the refresh is started
 * and the current token handed out at once — waiting there would stall every
 * request in this tab for as long as another tab holds the lock.
 */
export const ensureFreshToken = async () => {
  const token = getAccessToken();
  if (ending || !token) return null;

  if (isExpired(token, EXPIRY_SKEW_MS)) {
    await refreshOnce();
  } else if (isRefreshDue()) {
    refreshOnce();
  }

  return getAccessToken();
};

const catchUp = () => {
  if (catchUpTimer) clearTimeout(catchUpTimer);

  catchUpTimer = setTimeout(() => {
    catchUpTimer = null;
    if (isRefreshDue()) refreshOnce();
  }, CATCH_UP_DELAY_MS);
};

const onVisibilityChange = () => {
  if (document.visibilityState === 'visible') catchUp();
};

const onRemoteEvent = (message) => {
  if (message.type === SESSION_EVENTS.TOKENS_UPDATED) {
    clearBackoff();
    if (reauthRequest) {
      finishReauth(true);
      startSession();
    }
    return;
  }

  if (message.type === SESSION_EVENTS.SESSION_ENDED) {
    endSession(message.reason, { broadcast: false });
  }
};

let unsubscribeRemote = null;

function stopScheduler() {
  if (checkTimer) clearInterval(checkTimer);
  if (catchUpTimer) clearTimeout(catchUpTimer);
  checkTimer = null;
  catchUpTimer = null;

  if (!started) return;

  document.removeEventListener('visibilitychange', onVisibilityChange);
  window.removeEventListener('focus', catchUp);
  window.removeEventListener('online', catchUp);
  started = false;
}

/** Starts the proactive refresh schedule. Idempotent. */
export const startSession = () => {
  ending = false;

  if (started) {
    catchUp();
    return;
  }

  if (!unsubscribeRemote) unsubscribeRemote = onSessionEvent(onRemoteEvent);

  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('focus', catchUp);
  window.addEventListener('online', catchUp);

  checkTimer = setInterval(() => {
    if (isRefreshDue()) refreshOnce();
  }, CHECK_INTERVAL_MS);

  started = true;
  catchUp();
};

/** Diagnostics for the debug panel. Never exposes token material. */
export const getSessionInfo = () => {
  const token = getAccessToken();

  return {
    hasAccessToken: !!token,
    hasRefreshToken: !!getRefreshToken(),
    tokenVersion: getTokenVersion(),
    msUntilExpiry: getMsUntilExpiry(token),
    refreshDue: isRefreshDue(),
    refreshing: inflight !== null,
    reauthPending: reauthRequest !== null,
    networkFailures,
    blockedForMs: Math.max(blockedUntil - Date.now(), 0)
  };
};
